import { NextPageContextWithRedux } from '@/types/store/NextPageContextWithRedux';
import { fetchUser } from '@/api/user';
import { fetchUserPublications } from '@/api/publication';
import withUser from '@/withUser';
import PublicationCard from '@components/app/PublicationCard';
import { Button, Center, Heading, Icon, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { SearchResult } from '@/types/search/searchresult';
import { Publication } from '@/types/core/Publication';
import Head from 'next/head';
import Header from '@components/Header';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import FrontLayout from '@layouts/FrontLayout';
import MemberIDCard from '@components/app/cards/MemberIDCard';
import UIWidth from '@components/presentational/UIWidth';
import { PiPlusThin } from 'react-icons/pi';
import ShowUserProfile from '@components/profile/ShowUserProfile';
import { User } from '@/types/core/User';
import Error from 'next/error';

const ITEMS_PER_PAGE = 10;

function PageProfileUser({
  searchPublications,
  user,
}: {
  searchPublications: SearchResult<Publication>;
  user: User | null;
}) {
  const isFirstRender = useRef(true);

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [publications, setPublications] = useState<SearchResult<Publication>>(
    searchPublications
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsLoading(true);
    fetchUserPublications(user.id, ITEMS_PER_PAGE, page, {}).then(result => {
      setIsLoading(false);
      setPublications({
        resultNumber: result.resultNumber,
        items: [...publications.items, ...result.items],
      });
    });
  }, [page]);

  if (!user) {
    return <Error statusCode={404} />;
  }

  return (
    <>
      <FrontLayout>
        <Head>
          <title>{user.displayName} | IFprofs</title>
        </Head>
        <Header />
        <Breadcrumb
          breadcrumbItems={[
            { label: 'Accueil', href: '/' },
            { label: 'Membres', href: '/communautes/membres' },
            { label: user.displayName },
          ]}
        >
          <BreadcrumbItem label="Accueil" as={'/'} href={'/'} />
          <BreadcrumbItem
            label="Membres"
            as={'communautes/membres'}
            href={'communautes/membres'}
          />
          <BreadcrumbItem label={user.displayName} />
        </Breadcrumb>
        <UIWidth>
          <Heading
            as="h1"
            color="brand.100"
            fontSize={['2rem', '', '3.1875rem']}
            mb={['1.4rem', '', '2.8rem']}
          >
            {user.displayName}
          </Heading>

          <VStack
            align="stretch"
            spacing={{ base: '1rem', md: '2rem' }}
            mb={{ base: '1rem', md: '2rem' }}
          >
            <MemberIDCard
              showDetailLink={false}
              user={user}
              isCardFullWidth={true}
              isMemberExpert={user.isExpert}
            ></MemberIDCard>

            <ShowUserProfile user={user} />
            <VStack spacing="1rem" align="stretch">
              <VStack spacing="1rem">
                {publications.items &&
                  publications.items.map(publication => (
                    <PublicationCard
                      key={publication.id}
                      publication={publication}
                      showFooter
                    />
                  ))}
              </VStack>
              {publications.items.length < publications.resultNumber && (
                <Center>
                  <Button
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    cursor={isLoading ? 'not-allowed' : 'pointer'}
                  >
                    <Icon as={PiPlusThin} boxSize={7} mr="0.625rem" />
                    Voir plus
                  </Button>
                </Center>
              )}
            </VStack>
          </VStack>
        </UIWidth>
      </FrontLayout>
    </>
  );
}

PageProfileUser.getInitialProps = async (context: NextPageContextWithRedux) => {
  const userId = context.query.user as string;
  const currentUser = context.reduxStore.getState().authentication.user;

  const [user, searchPublications] = await Promise.all([
    fetchUser(
      {
        fields: {
          user: `displayName,introduction,countPublications:,jobs,roles,email,disciplines,jobs,unreadMessages,unreadAlerts,countPublications:,countColleagues,hasCompletedAllRecommendedFields,emailIFPCommunication,emailNetworkEvents,publicEmail,school,schoolType,address,address2,postcode,town,isExpert,isColleague:${currentUser.id},colleagueState:${currentUser.id}`,
          country: 'name,code,primaryColor,secondaryColor',
          partnership:
            'businessSector,activities,otherActivities,name,structureType',
          file: 'filename',
          userWebsite: 'title,url',
          expertise: 'slug',
        },
        relationIncludes: [
          'user',
          'country',
          'avatar',
          'partnership',
          'websites',
          'expertise',
        ],
      },
      userId,
      { context }
    ),
    fetchUserPublications(
      userId,
      ITEMS_PER_PAGE,
      1,
      {
        publishedStatus: 'state.published',
      },
      { context }
    ),
  ]);

  return {
    user,
    searchPublications,
  };
};
export default withUser(PageProfileUser, { requireLoggedUser: true });
